<template>
    <page-container>
        <page-header>
            Automations

            <template v-slot:actions>
                <vx-button
                    @click="showOverview = !showOverview"
                    data-test="toggle-overview"
                    type="button"
                    color="muted-dark"
                    size="lg"
                >
                    <span class="hidden sm:inline-block">{{ showOverview ? 'Hide' : 'Show' }} Overview</span>
                    <font-awesome-icon
                        widthAuto
                        class="hidden! sm:inline-block!"
                        :icon="faCircleQuestion"
                    ></font-awesome-icon>
                    <font-awesome-icon
                        widthAuto
                        class="inline-block! sm:hidden!"
                        :icon="showOverview ? faCircleChevronUp : faCircleChevronDown"
                    ></font-awesome-icon>
                </vx-button>
            </template>
        </page-header>

        <automations-overview v-show="showOverview" class="mb-14"></automations-overview>

        <automation-rule-controls
            v-model="fetchParams"
            @apply="applyFilters"
            @reset="resetFilters"
            :loading="loading"
        ></automation-rule-controls>

        <div v-if="loading && !list.length" class="text-center py-10">
            <vx-icon-loading class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
        </div>

        <template v-else>
            <vx-box
                color="info"
                v-if="list.length === 0 && !isEqual(defaultFilters(), fetchParams)"
                class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
            >
                <div class="w-64 mx-auto text-center">No results for current search, try adjusting your filters.</div>
                <vx-button type="button" color="secondary" @click="resetFilters">
                    Reset All <font-awesome-icon widthAuto :icon="faCircleX" />
                </vx-button>
            </vx-box>
            <vx-box
                color="info"
                v-else-if="totalCount === 0"
                class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
            >
                No automations yet.
                <vx-button href="/automation/event/new">
                    New Automation <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
            </vx-box>
            <template v-else>
                <vx-table
                    v-if="list.length"
                    :class="FEATURES.flows ? 'xl:grid-cols-[repeat(7,auto)]' : 'xl:grid-cols-[repeat(6,auto)]'"
                    rowBreakpoint="xl"
                >
                    <vx-table-row isHeader>
                        <div>Name</div>
                        <div>Trigger Event</div>
                        <div>Filters</div>
                        <div>Actions</div>
                        <div>Updated</div>
                        <div v-if="FEATURES.flows">Flow</div>
                    </vx-table-row>
                    <automation-rule-row
                        v-for="(item, index) in list"
                        v-model="list[index]"
                        :key="item.id"
                        @deleted="removeFromList($event)"
                    ></automation-rule-row>
                </vx-table>

                <div v-if="list.length" class="mt-8 flex justify-between w-full gap-10">
                    <vx-button
                        v-if="fetchParams.cursor"
                        @click="loadData()"
                        color="muted-dark"
                        size="xl"
                        class="grow-0"
                        :loading="loading"
                        data-test="load-more-btn"
                    >
                        Load More
                        <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
                    </vx-button>
                    <div class="text-lg grow-0 ml-auto font-normal text-slate-600">
                        {{ list.length }}/{{ totalCount }}
                    </div>
                </div>
            </template>
        </template>
    </page-container>
</template>
<script setup>
import { ref, onMounted, inject } from 'vue';
import {
    faCircleChevronDown,
    faCircleChevronUp,
    faCircleQuestion,
    faSpinner,
    faCircleX,
    faCirclePlus,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxButton, VxIconLoading, VxTable, VxTableRow } from '@voxie/frontend-components';
import PageContainer from '~/components/general/PageContainer.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import AutomationsOverview from '~/components/automation/automations/components/AutomationsOverview.vue';
import AutomationRuleControls from '~/components/automation/automations/components/AutomationRuleControls.vue';
import AutomationRuleRow from '~/components/automation/AutomationRuleRow.vue';

import { automationRulesClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import { isEqual } from '~/utils/helpers';
import { useCursorPagination } from '../../composables/useCursorPagination';

const FEATURES = inject('FEATURES');

const teamId = Spark.state.currentTeam.id;
const toasted = useToasted();
const showOverview = ref(true);

const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const defaultFilters = () => ({
    query: '',
    per_page: 10,
    cursor: null,
    flow: {
        in_flow: undefined,
        flow_id: null,
    },
    category: undefined,
    sort_field: 'id',
    sort_dir: 'desc',
});

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: undefined,
    };

    loadData();
};

const totalCount = ref(0);

const loadData = () => {
    fetchRecords(automationRulesClient.search(teamId, { ...fetchParams.value }));
    fetchTotalCount();
};

const fetchTotalCount = async () => {
    try {
        const response = await automationRulesClient.automationRulesCount(teamId, { ...fetchParams.value });
        totalCount.value = response.data?.count || 0;
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    }
};

const removeFromList = (ruleId) => {
    list.value = list.value.filter((item) => item.id != ruleId);
    totalCount.value = totalCount.value - 1;

    if (!list.value.length) {
        loadData();
    }
};

onMounted(() => {
    resetFilters();
});
</script>
